/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { Trash2 } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { mutate } from 'swr';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

import { IWithPagination, UserDataWithRole } from '@/commons/commons';
import Pagination from '@/components/list/Pagination';
import { useGet, useMutate, useUser } from '@/net/commons';
import { cn } from '@/utils';

type MemberRowActionsProps = {
    member: UserDataWithRole;
    toggleRemove: () => void;
};

const MemberRowActions = ({ member, toggleRemove }: MemberRowActionsProps) => {
    return (
        <AlertDialog>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex h-7 w-7 p-0 data-[state=open]:bg-muted">
                        <DotsHorizontalIcon className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[160px]">
                    <AlertDialogTrigger asChild>
                        <DropdownMenuItem className="text-destructive">
                            <div className="flex w-full justify-between items-center">
                                Remove member
                                <Trash2 size={14} />
                            </div>
                        </DropdownMenuItem>
                    </AlertDialogTrigger>
                </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Remove member</AlertDialogTitle>
                    <AlertDialogDescription>
                        Are you sure you want to remove <span className="font-semibold text-black">{member.email}</span>
                        ?
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={toggleRemove}>
                        Remove
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default function MembersTable() {
    const [searchParams] = useSearchParams();
    const page = Number(searchParams.get('page')) || 1;
    const pageSize = Number(searchParams.get('pageSize')) || 20;
    const { data: members } = useGet<IWithPagination<UserDataWithRole>>(
        `/bff/organisation/members?page=${page}&pageSize=${pageSize}`,
    );
    const { trigger } = useMutate('/bff/organisation/members/:id', {
        method: 'DELETE',
    });
    const { user } = useUser();

    const shouldShowPagination = (members?.pagination?.total || 0) > pageSize;

    const onRemove = async (id: number) => {
        await trigger({ idParam: id });
        mutate((key) => typeof key === 'string' && key.startsWith('/bff/organisation/members'), undefined, {
            revalidate: true,
        });
    };

    return (
        <>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[45%]">User</TableHead>
                        <TableHead className="w-[25%]">Joined</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {members?.data.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground">
                                There are no items to display.
                            </TableCell>
                        </TableRow>
                    )}
                    {members?.data.map((row) => {
                        const fullName =
                            row.firstName || row.lastName ? `${row.firstName} ${row.lastName}`.trim() : null;
                        return (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <div className="flex flex-col">
                                        <div className="flex gap-2 items-center">
                                            {fullName && (
                                                <span className="capitalize text-sm font-medium">{fullName}</span>
                                            )}
                                            {row.id === user?.id && (
                                                <Badge
                                                    variant="outline"
                                                    className="bg-accent text-accent-foreground px-1.5 py-[1px]"
                                                >
                                                    You
                                                </Badge>
                                            )}
                                        </div>
                                        <span className={cn(fullName ? 'text-xs' : 'text-sm font-medium')}>
                                            {row.email}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell className="text-xs">
                                    {new Date(row.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell className="flex justify-end">
                                    {row.id !== user?.id && (
                                        <MemberRowActions member={row} toggleRemove={() => onRemove(row.id)} />
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            {members?.pagination && shouldShowPagination && <Pagination {...members.pagination} />}
        </>
    );
}
